'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomLink from '@/components/ui/common/custom-link';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '@/components/ui/layout';
import {
    buildStaticAssetPath,
    getStaticAssetUrl,
} from '@/app/lib/utils/static-asset-url';

function Mail() {
    const { t } = useTranslation('mail');
    const locale = useLocale();
    const [isOpenMailMore, setIsOpenMailMore] = useState(false);
    const toggleButtonRef = useRef<HTMLButtonElement>(null); // 引用切换按钮
    const scrollPosition = useRef(0); // 用来保存滚动位置

    const sections: {
        isBlod: boolean;
        content: string;
        isLink: boolean;
        href: string;
    }[][] = t('mail.content.sections', {
        returnObjects: true,
    }) as {
        isBlod: boolean;
        content: string;
        isLink: boolean;
        href: string;
    }[][];
    const mobileBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('about', 'mobile-bg-mail.webp'),
    );
    const desktopBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('about', 'bg-mail.webp'),
    );
    const embellishBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('about', 'embellish.webp'),
    );

    useEffect(() => {
        if (isOpenMailMore) {
            scrollPosition.current = window.scrollY;
        } else {
            if (scrollPosition.current == 0) {
                return;
            }

            window.scrollTo({
                top: scrollPosition.current,
                behavior: 'smooth',
            });
        }
    }, [isOpenMailMore]);

    return (
        <div className="relative flex w-full flex-col items-center overflow-hidden">
            <div
                className="tablet:hidden absolute left-0 top-0 h-full w-full bg-cover bg-top bg-no-repeat"
                style={{
                    backgroundImage: `url(${mobileBackgroundImageUrl})`,
                }}
            />

            <div
                className="tablet:block absolute left-0 top-0 hidden h-full w-full bg-cover bg-top bg-no-repeat"
                style={{
                    backgroundImage: `url(${desktopBackgroundImageUrl})`,
                }}
            />

            <div
                className="tablet:flex absolute left-0 top-[120px] hidden h-[578px] w-full items-center bg-top bg-no-repeat"
                style={{
                    backgroundImage: `url(${embellishBackgroundImageUrl})`,
                }}
            />

            <h1
                className={clsx(
                    'tablet:mt-[220px] z-10 mt-[140px] px-[21px] text-[42px] tracking-[-0.01em] ',
                    {
                        'font-libre-bodoni tablet:text-[76px] tablet:leading-[88px] text-center font-black leading-[54px]':
                            locale === 'en',
                        'font-pingfang tablet:text-[68px] tablet:leading-[102px] text-center font-bold leading-[63px]':
                            locale === 'zh',
                    },
                )}
            >
                <div>{t('slogan.p1')}</div>
                <div>{t('slogan.p2')}</div>
                <div>{t('slogan.p3')}</div>
                {t('slogan.p4').length > 0 ? <p>{t('slogan.p4')}</p> : ''}
            </h1>

            <JContainer
                size="lg"
                padding="md"
                className="z-10 mb-[70px] mt-[140px] flex flex-col justify-center sm:mb-[130px] sm:mt-[180px]"
            >
                <div
                    className={clsx(
                        'j-t1 w-full rounded-[16px] bg-white p-[24px] shadow-md  sm:p-[40px]',
                    )}
                >
                    <h2 className={clsx('j-h3')}>
                        {t('mail.title.p1')} <br />
                        {t('mail.title.p2')}
                    </h2>
                    <div className={clsx('j-t1 mt-[40px]')}>
                        {t('mail.content.sayHi')}
                    </div>
                    <div className={clsx('j-t1 mt-[20px]')}>
                        {sections.map((section, index) => (
                            <div
                                key={'section-' + index}
                                className={clsx('', {
                                    hidden: !isOpenMailMore && index > 1,
                                })}
                            >
                                <p>
                                    {section.map((line, index) =>
                                        line.isLink ? (
                                            <span key={'link-' + index}>
                                                &nbsp;
                                                <CustomLink href={line.href}>
                                                    <span
                                                        className={clsx(
                                                            'inline',
                                                            {
                                                                'j-h7': line.isBlod,
                                                                'j-t1': !line.isBlod,
                                                            },
                                                        )}
                                                    >
                                                        {line.content}
                                                    </span>
                                                </CustomLink>
                                                &nbsp;
                                            </span>
                                        ) : (
                                            <span
                                                key={'section-text-' + index}
                                                className={clsx(
                                                    'inline',
                                                    line.isBlod
                                                        ? 'j-h7'
                                                        : 'j-t1',
                                                )}
                                            >
                                                {line.content}
                                            </span>
                                        ),
                                    )}
                                </p>
                                <br />
                            </div>
                        ))}
                    </div>

                    <button
                        ref={toggleButtonRef}
                        onClick={() => {
                            setIsOpenMailMore((prev) => !prev);
                        }}
                        className={clsx('j-t1 text-[#6C49FF]')}
                    >
                        {isOpenMailMore
                            ? t('mail.moreButton.close')
                            : t('mail.moreButton.open')}
                    </button>

                    <img
                        alt="Kevin Signature"
                        src={getStaticAssetUrl('/kevin-signature.webp')}
                        className="mt-[25.8px] w-[107.5px]"
                    />

                    <div className={clsx('j-p1 mt-[8px]')}>
                        {t('mail.signature')}&nbsp;&nbsp;｜&nbsp;&nbsp;
                        {t('mail.date')}
                    </div>
                </div>
            </JContainer>
        </div>
    );
}

export default Mail;
